import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BUSINESS } from "@/lib/business";

const links = [
  { id: "home", label: "Home" },
  { id: "services", label: "Services" },
  { id: "about", label: "About" },
  { id: "contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => scrollTo("home")}
          className="flex items-center gap-3 group"
        >
          <img
            src={BUSINESS.logoUrl}
            alt="Perficient Building Services Ltd"
            className="w-10 h-10 sm:w-11 sm:h-11 rounded-md object-cover bg-[#0A192F]"
          />
          <span className="font-heading font-semibold text-[#0A192F] text-sm sm:text-base tracking-tight leading-tight">
            Perficient
            <span className="hidden sm:inline"> Building Services Ltd</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-2">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="px-4 py-2 text-sm font-medium text-[#334155] hover:text-[#0A192F] hover:bg-slate-100 rounded-md transition-colors"
            >
              {l.label}
            </button>
          ))}
          <a
            href={BUSINESS.emailHref}
            data-testid="nav-email-button"
            className="ml-3 inline-flex items-center gap-2 bg-[#0A192F] text-white text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-[#1E3A8A] transition-colors"
          >
            <Mail className="w-4 h-4" strokeWidth={1.8} />
            {BUSINESS.email}
          </a>
        </nav>

        <Button
          variant="ghost"
          size="icon"
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          className="md:hidden text-[#0A192F]"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden overflow-hidden bg-white border-b border-slate-200"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => scrollTo(l.id)}
                  className="text-left px-3 py-3 rounded-md text-[#0A192F] hover:bg-slate-100 font-medium"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={BUSINESS.emailHref}
                data-testid="nav-mobile-email-button"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white text-sm font-semibold px-4 py-3 rounded-md"
              >
                <Mail className="w-4 h-4" />
                Email {BUSINESS.email}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
